import React, { useRef, useState, useEffect } from "react";
import {
  Box,
  Divider,
  Grid,
  List,
  ListItemButton,
  ListItemText,
  Typography,
  useTheme,
  Paper,
  Fade,
  Grow,
  useMediaQuery,
  AppBar,
  IconButton,
  Toolbar,
} from "@mui/material";
import { motion } from "framer-motion";
import { ColorModeContext, tokens } from "../../theme";
import { useBranding } from "../../BrandingContext";
import { DarkModeOutlined, LightModeOutlined } from "@mui/icons-material";

const sections = [
  { id: "purpose", label: "Purpose" },
  { id: "responsibility", label: "User Responsibility" },
  { id: "optout", label: "Opting Out" },
  { id: "help", label: "Technical Help" },
  { id: "disclaimer", label: "Disclaimer of Liability" },
  { id: "fees", label: "Fees and Charges" },
  { id: "privacy", label: "Privacy and Data Security" },
];

const Terms2fa: React.FC = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const branding = useBranding();
  const colorMode = React.useContext(ColorModeContext);
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const sectionRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const [activeSection, setActiveSection] = useState("purpose");
  const [highlighted, setHighlighted] = useState<string | null>(null);

  const logoSrc =
    theme.palette.mode === "dark"
      ? branding?.logoWhite ||
        require("../../branding-assets/v3biomed/white-logo.png")
      : branding?.logo || require("../../branding-assets/v3biomed/logo.png");

  const scrollToSection = (id: string) => {
    const section = sectionRefs.current[id];
    if (section) {
      const yOffset = -100;
      const y = section.getBoundingClientRect().top + window.scrollY + yOffset;
      window.scrollTo({ top: y, behavior: "smooth" });
      setHighlighted(id);
      setActiveSection(id);
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      const offset = 120;
      let current = "purpose";

      for (const { id } of sections) {
        const ref = sectionRefs.current[id];
        if (ref) {
          const sectionTop = ref.offsetTop - offset;
          if (scrollPosition >= sectionTop) {
            current = id;
          }
        }
      }
      setActiveSection(current);
      setHighlighted(current);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <Box
      sx={{
        pt: 12,
        pb: 10,
        minHeight: "100vh",
        bgcolor: theme.palette.background.default,
      }}
    >
      <AppBar
        position="fixed"
        elevation={1}
        sx={{
          background:
            theme.palette.mode === "dark"
              ? "linear-gradient(to right,rgb(39, 39, 39), #1e293b)"
              : "linear-gradient(to right, #e0f7fa, #ffffff)",
          color: theme.palette.text.primary,
        }}
      >
        <Toolbar sx={{ justifyContent: "space-between", px: 2 }}>
          <Box display="flex" alignItems="center">
            <img
              src={logoSrc}
              alt="V3Biomed Logo"
              style={{ height: 40, objectFit: "contain" }}
            />
          </Box>
          <IconButton onClick={colorMode.toggleColorMode} color="inherit">
            {theme.palette.mode === "dark" ? (
              <LightModeOutlined />
            ) : (
              <DarkModeOutlined />
            )}
          </IconButton>
        </Toolbar>
      </AppBar>

      <Grid container spacing={4} px={{ xs: 2, md: 8 }}>
        {!isMobile && (
          <Grid item md={3}>
            <Fade in timeout={700}>
              <Box
                sx={{
                  position: "sticky",
                  top: 100,
                  p: 3,
                  borderRadius: 4,
                  background:
                    theme.palette.mode === "dark"
                      ? "linear-gradient(to right,rgb(39, 39, 39), #1e293b)"
                      : "linear-gradient(to right, #e0f7fa, #ffffff)",
                  border: "2px solid rgba(110, 110, 110, 0.3)",
                  boxShadow: 3,
                }}
              >
                <List>
                  {sections.map((section) => {
                    const isSelected = activeSection === section.id;
                    return (
                      <ListItemButton
                        key={section.id}
                        onClick={() => scrollToSection(section.id)}
                        selected={isSelected}
                        sx={{
                          borderRadius: 2,
                          transition: "all 0.3s",
                          mb: 1,
                          color: isSelected
                            ? colors.success[300]
                            : colors.grey[300],
                          fontWeight: 600,
                          "&:hover": {
                            pl: 2,
                            bgcolor: colors.grey[700],
                            color: colors.grey[100],
                          },
                          "&.Mui-selected": {
                            bgcolor: colors.success[500],
                            color: colors.success[100],
                            fontWeight: 600,
                          },
                        }}
                      >
                        <ListItemText
                          primary={section.label}
                          primaryTypographyProps={{ fontWeight: 600 }}
                        />
                      </ListItemButton>
                    );
                  })}
                </List>
              </Box>
            </Fade>
          </Grid>
        )}

        <Grid item xs={12} md={9}>
          <Grow in timeout={900}>
            <Paper
              elevation={6}
              sx={{
                borderRadius: 4,
                p: { xs: 3, md: 6 },
                background:
                  theme.palette.mode === "dark"
                    ? "linear-gradient(to right,rgb(39, 39, 39), #1e293b)"
                    : "linear-gradient(to right, #e0f7fa, #ffffff)",
                border: "2px solid rgba(110, 110, 110, 0.3)",
                backdropFilter: "blur(12px)",
                boxShadow:
                  theme.palette.mode === "dark"
                    ? "0 0 30px rgba(0,0,0,0.4)"
                    : "0 0 24px rgba(0,0,0,0.1)",
              }}
            >
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <Typography
                  variant="h3"
                  fontWeight={900}
                  align="center"
                  gutterBottom
                  sx={{ fontSize: { xs: "2rem", md: "2.75rem" } }}
                >
                  Terms and Conditions for 2FA via SMS
                </Typography>
                <Divider sx={{ my: 4 }} />

                {sections.map(({ id, label }) => (
                  <motion.div
                    key={id}
                    ref={(el) => (sectionRefs.current[id] = el)}
                    id={id}
                    style={{ scrollMarginTop: 120 }}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.2 }}
                    transition={{ duration: 0.5, ease: "easeOut" }}
                  >
                    <motion.div
                      initial={{ backgroundColor: "transparent" }}
                      animate={{
                        backgroundColor:
                          highlighted === id
                            ? theme.palette.mode === "dark"
                              ? "rgba(100, 255, 218, 0.05)"
                              : "rgba(255, 255, 0, 0.15)"
                            : "transparent",
                      }}
                      transition={{ duration: 1 }}
                      style={{ borderRadius: 12, padding: 16 }}
                    >
                      <Typography variant="h4" fontWeight={800} gutterBottom>
                        {label}
                      </Typography>
                      <Typography
                        variant="h6"
                        color={colors.grey[400]}
                        paragraph
                      >
                        {getSectionContent(id, branding)}
                      </Typography>
                    </motion.div>
                  </motion.div>
                ))}
              </motion.div>
            </Paper>
          </Grow>
        </Grid>
      </Grid>
    </Box>
  );
};

const getSectionContent = (id: string ,branding:any): React.ReactNode | null => {
  const contentMap: Record<string, React.ReactNode> = {
    purpose: (
      <>
        The Two-Factor Authentication (2FA) SMS service provides an added layer
        of security by sending a one-time passcode (OTP) to the user’s
        registered mobile phone number. This feature offers an additional option
        to receive the code on your phone along with the email address. The
        mobile number and related information provided for the 2FA SMS service
        will be used solely for authentication purposes. All personal data will
        be handled in accordance with our{" "}
        <a
          // href="/privacy.html"
          href={branding?.privacyPolicyUrl}//CS-1602 - making dynamic privacy policy linkfrom branding config
          target="_blank"
          rel="noopener noreferrer"
          style={{
            color: "#4fc3f7",
            textDecoration: "underline",
            fontWeight: 500,
          }}
        >
          Privacy Policy
        </a>
        .
      </>
    ),
    responsibility: `Users must provide a valid and active mobile phone number to receive 2FA SMS messages.
          Users are responsible for maintaining the confidentiality of the OTP and ensuring it is not shared with unauthorized individuals.
          Users agree to notify us immediately if they suspect any unauthorized access or misuse of the OTP.`,
    optout: `Users may opt out of the 2FA SMS service by updating their account settings or contacting the ${branding?.companyName || "V3Biomed"} support team or their assigned sales representative.
          Opting out may impact the security of the account.
          You can cancel the SMS service at any time by texting "STOP" to the short code. After sending "STOP," we will confirm your unsubscription via SMS. You will no longer receive SMS messages from us. To rejoin, sign up as you did initially, and SMS messages will resume.`,
    help: `If you are experiencing issues with the messaging program, you can reply with the keyword HELP for assistance, or contact your assigned sales representative directly.`,
    disclaimer: `Carriers are not liable for delayed or undelivered messages.
          The company is not liable for any loss or damage arising from the use of, or inability to use, the 2FA SMS service.`,
    fees: `Standard messaging rates may apply, depending on the user’s mobile carrier and plan.
          The company is not responsible for any charges incurred due to receiving 2FA SMS messages. For questions about your text or data plan, contact your wireless provider.`,
    privacy: (
      <>
        The mobile number and related information provided for the 2FA SMS
        service will be used solely for authentication purposes.
        <br />
        All personal data will be handled in accordance with our{" "}
        <a
          // href="/privacy.html"
          href={branding?.privacyPolicyUrl}//CS-1602 - making dynamic privacy policy linkfrom branding config
          target="_blank"
          rel="noopener noreferrer"
          style={{
            color: "#4fc3f7",
            textDecoration: "underline",
            fontWeight: 500,
          }}
        >
          Privacy Policy
        </a>
        .
      </>
    ),
  };

  return contentMap[id] || null;
};

export default Terms2fa;
